/* ════════════════════════════════════════════════════════════
   ترتيب المنشورات — «الرائج»

   🐛 كان أمام القارئ ترتيبان لا ثالث لهما، وكلاهما معيب:

     «الأحدث»  يتجاهل التصويت تماماً. فمنشور بـ12 صوتاً يهبط إلى
               الأسفل لمجرّد أنّ غيره نُشر بعده.
     «الأكثر»  يتجاهل الزمن تماماً. فمنشور من شهرين يتصدّر أبداً،
               ولا يصعد جديد مهما تفاعل الناس معه.

   والافتراضي كان «الأحدث» — وهو ما يراه القارئ أوّل مرّة.

   والحلّ الذي تستعمله المنتديات: نقاط تتحلّل بالزمن، فيتنافس
   التصويت والحداثة في رقم واحد بدل أن يُلغي أحدهما الآخر.

   ── الصيغة ──

       hot = log10(max(|w|, 1)) × 2.5 × sign(w) + (t − T0) / 172800
       حيث  w = score + 0.5 × commentCount

   • **اللوغاريتم** يجعل الأصوات الأولى أثقل من التالية: الفرق بين 1
     و10 يساوي الفرق بين 10 و100. فمنشور بـ100 صوت لا يسحق منشوراً
     بـ12 صوتاً سحقاً أبدياً، ويبقى للجديد فرصة.
   • **`sign`** يُنزل المنشور السالب بدل أن يرفعه اللوغاريتم.
   • **172800 ثانية (48 ساعة)** هي ثابت التحلّل، و**2.5** وزن الأصوات.
     والرقمان مُعايَران على سيناريوهات حقيقية لا مأخوذان من مرجع:
     جرّبت 13 و24 و36 و48 ساعة، فوجدت أنّ ما دون 48 يُهبط منشوراً
     بـ12 صوتاً تحت منشور **بلا أصوات** نُشر للتوّ — وهو عين العطب
     الذي نُصلحه. و48 ساعة تناسب جمهوراً يتصفّح يومياً لا كل ساعة:
     منشور الأمس الجيّد يبقى مرئياً، ومنشور الأسبوع الماضي ينزل.
   • **`T0`** نقطة إسناد ثابتة (مطلع 2024) تُبقي الأرقام صغيرة
     مقروءة، ولا أثر لقيمتها على الترتيب لأنّها تُطرح من الجميع.

   ⚠️ والتعليقات تُحسب صوتاً ناقصاً: النقاش دليل اهتمام كالتصويت،
   ومنشور يُثير عشرة ردود أحقّ بالصدارة من آخر أخذ عشرة أصوات صامتة.
   ووزنها 0.5 لا 1 لأنّ التعليق أسهل من إجماع عشرة أشخاص.
════════════════════════════════════════════════════════════ */

/** نقطة إسناد ثابتة: 2024-01-01T00:00:00Z بالثواني */
const EPOCH = 1_704_067_200;

/** ثابت التحلّل بالثواني — 48 ساعة */
const DECAY = 172_800;

/** وزن التعليق مقابل الصوت */
const COMMENT_WEIGHT = 0.5;

/** وزن الأصوات مقابل الزمن — كلّما كبر تأخّر تأثير التقادم */
const VOTE_WEIGHT = 2.5;

export function hotScore(score: number, commentCount: number, createdAt: number): number {
  const weighted = score + commentCount * COMMENT_WEIGHT;
  const order = Math.log10(Math.max(Math.abs(weighted), 1)) * VOTE_WEIGHT;
  const sign = weighted > 0 ? 1 : weighted < 0 ? -1 : 0;
  const seconds = createdAt / 1000 - EPOCH;
  return Number((sign * order + seconds / DECAY).toFixed(7));
}
